import {
  bulkPutLikers,
  listAllCached,
  type LikerRecordRow,
  type LikerSourceKind,
} from '../cache/db'
import { activeAccount } from '../store/accounts'
import { COLLECTIONS } from './pull'
import { api, withFreshAccess } from './client'

const PAGE_LIMIT = 100
const PARALLEL = 4 // concurrent getLikes requests

interface GetLikesResp {
  likes?: Array<{
    actor: {
      did: string
      handle: string
      displayName?: string
      avatar?: string
    }
    createdAt?: string
    indexedAt?: string
  }>
  cursor?: string
}

export interface LikersPullProgress {
  source: LikerSourceKind
  /** Number of source posts processed (likes pulled into the cache). */
  postsDone: number
  /** Total source posts to process for this run. */
  postsTotal: number
  /** Cumulative new like-rows written to the cache so far. */
  likersAdded: number
  error?: string
}

interface PullOpts {
  targetHandle: string
  sources: LikerSourceKind[]
  /** Don't re-walk a post we've already pulled at least once. */
  skipAlreadyPulled?: boolean
  signal?: AbortSignal
  onProgress?: (p: LikersPullProgress) => void
}

/**
 * "Likes of likes": for each cached post / reply / repost record of
 * the target, walk app.bsky.feed.getLikes pagination and write every
 * actor → liker_records.
 *
 * The auth account is the active account (we need a session to call
 * getLikes through the worker proxy). The TARGET is whoever's handle
 * was passed in.
 */
export async function pullLikersFor(opts: PullOpts): Promise<void> {
  const acc = activeAccount()
  if (!acc) return
  const handle = opts.targetHandle.toLowerCase()

  for (const source of opts.sources) {
    if (opts.signal?.aborted) return
    const collection =
      source === 'reposts' ? COLLECTIONS.reposts : COLLECTIONS.posts
    const cached = await listAllCached(handle, collection)

    // Resolve which records are actually IN this source.
    const filtered = cached.filter((r) => {
      if (source === 'reposts') return true
      const isReply = !!(r.value as { reply?: unknown }).reply
      return source === 'replies' ? isReply : !isReply
    })

    // Source URIs to walk likes for. Reposts → original-post subject URI.
    const targets = filtered
      .map((r) => {
        if (source === 'reposts') {
          const sub = (r.value as { subject?: { uri?: string } }).subject?.uri
          return sub ?? null
        }
        return r.uri
      })
      .filter((u): u is string => !!u)

    let postsDone = 0
    let likersAdded = 0
    // Sticky: once any post in this source fails, keep the message on
    // every subsequent tick so the UI can show it (a transient error
    // used to be wiped by the next successful tick).
    let lastError: string | undefined
    const tick = (extra?: Partial<LikersPullProgress>) =>
      opts.onProgress?.({
        source,
        postsDone,
        postsTotal: targets.length,
        likersAdded,
        error: lastError,
        ...extra,
      })
    tick()

    // Limited concurrency over the post URIs.
    let cursor = 0
    const workers = Array.from({ length: PARALLEL }, () => worker())
    await Promise.all(workers)

    async function worker() {
      while (true) {
        if (opts.signal?.aborted) return
        const i = cursor++
        if (i >= targets.length) return
        const uri = targets[i]
        try {
          const rows = await fetchAllLikesFor(handle, uri, source, opts.signal)
          if (rows.length > 0) {
            await bulkPutLikers(rows)
            likersAdded += rows.length
          }
          postsDone += 1
          tick()
        } catch (err) {
          lastError = err instanceof Error ? err.message : String(err)
          tick()
        }
      }
    }
  }
}

async function fetchAllLikesFor(
  handle: string,
  postUri: string,
  source: LikerSourceKind,
  signal?: AbortSignal,
): Promise<LikerRecordRow[]> {
  const acc = activeAccount()
  if (!acc) return []
  const out: LikerRecordRow[] = []
  let cursor: string | undefined
  while (true) {
    if (signal?.aborted) return out
    const qs = new URLSearchParams({
      uri: postUri,
      limit: String(PAGE_LIMIT),
    })
    if (cursor) qs.set('cursor', cursor)
    const r = await withFreshAccess(acc.id, () =>
      api.xrpc<GetLikesResp>({
        accountId: acc.id,
        method: 'GET',
        path: `app.bsky.feed.getLikes?${qs.toString()}`,
      }),
    )
    const ts = Date.now()
    for (const like of r.likes ?? []) {
      const a = like.actor
      out.push({
        id: `${handle}|${postUri}|${a.did}`,
        targetHandle: handle,
        postUri,
        sourceKind: source,
        likerDid: a.did,
        likerHandle: a.handle,
        likerDisplayName: a.displayName,
        likerAvatar: a.avatar,
        likedAt: like.createdAt ?? like.indexedAt ?? new Date(ts).toISOString(),
        fetchedAt: ts,
      })
    }
    cursor = r.cursor
    if (!cursor) return out
  }
}
